import React, { useState, useMemo, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import { speakTracked } from '../speech/speak'
import styles from './SpokenText.module.scss'

// Text that lights up word-by-word as it is spoken, and whose words can be
// tapped for a lookup. Reusable in any view: it only needs the text, the
// language code, a voice and a rate.
//
//   <SpokenText text="Ben otele gidiyorum." lang="tur" voice={voice} rate={0.9}
//               onWordClick={(word, event, sentence) => lookup(word, event, sentence)} />
//
// ref.play() speaks and returns a promise that resolves when done (so callers
// can chain sentences); ref.stop() cancels.

// No spaces between words → highlight and look up per character.
const CHAR_LANGS = new Set(['jpn', 'cmn', 'tha'])
// Syllable-block script with spaces (Korean) → per word, but speech is slower per character.
const SLOW_LANGS = new Set(['kor'])

export const cleanWord = (w) => String(w || '').replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')

export function tokenize(text, lang) {
  const s = String(text || '')
  const out = []
  if (CHAR_LANGS.has(lang)) {
    let i = 0
    for (const ch of s) { out.push({ text: ch, start: i, isWord: /[\p{L}\p{N}]/u.test(ch) }); i += ch.length }
    return out
  }
  const re = /\S+|\s+/g
  let m
  while ((m = re.exec(s))) out.push({ text: m[0], start: m.index, isWord: /[\p{L}\p{N}]/u.test(m[0]) })
  return out
}

const cpsFor = (lang) => (CHAR_LANGS.has(lang) || SLOW_LANGS.has(lang) ? 4.5 : 13)

// The word token that contains (or most recently started before) charIndex.
const tokenAt = (tokens, charIndex) => {
  let idx = -1
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].start > charIndex) break
    if (tokens[i].isWord) idx = i
  }
  return idx
}

const SpokenText = forwardRef(function SpokenText({ text, lang, voice, rate, onWordClick, showSpeaker = true, className = '', as: Tag = 'span' }, ref) {
  const tokens = useMemo(() => tokenize(text, lang), [text, lang])
  const [active, setActive] = useState(-1)
  const [playing, setPlaying] = useState(false)
  const handleRef = useRef(null)

  const play = useCallback(() => {
    if (handleRef.current) handleRef.current.cancel()
    setActive(-1)
    setPlaying(true)
    const h = speakTracked(text, voice, rate, {
      cps: cpsFor(lang),
      onBoundary: ({ charIndex }) => setActive(tokenAt(tokens, charIndex)),
      onEnd: () => { setPlaying(false); setActive(-1) },
    })
    handleRef.current = h
    return h.promise
  }, [text, voice, rate, lang, tokens])
  const stop = useCallback(() => { if (handleRef.current) handleRef.current.cancel() }, [])
  useImperativeHandle(ref, () => ({ play, stop }), [play, stop])
  useEffect(() => () => { if (handleRef.current) handleRef.current.cancel() }, [])

  return (
    <Tag className={`${styles.spoken} ${playing ? styles.playing : ''} ${className}`}>
      {tokens.map((t, i) => t.isWord
        ? (
          <span key={i}
            className={`${styles.tok} ${i === active ? styles.tokActive : ''} ${playing && active >= 0 && i < active ? styles.tokDone : ''}`}
            onClick={(e) => { e.stopPropagation(); if (onWordClick) onWordClick(cleanWord(t.text), e, text); else play() }}>
            {t.text}
          </span>
        )
        : <span key={i}>{t.text}</span>)}
      {showSpeaker && (
        <button type="button" className={styles.spk} onClick={(e) => { e.stopPropagation(); if (playing) stop(); else play() }} title={playing ? 'Stop' : 'Listen'}>
          <VolumeUpIcon fontSize="inherit" />
        </button>
      )}
    </Tag>
  )
})

export default SpokenText
